import React, { useState, useEffect, useMemo } from 'react';
import { Card, Row, Col, Typography, Table, Statistic, Spin, Tooltip } from 'antd';
import { 
  ComposedChart,
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';
import api from '../api';
import { useFilters } from '../contexts/FilterContext';
import { useTheme } from '../contexts/ThemeContext';
import API_CONFIG from '../config';
import FullscreenChart from './FullscreenChart';
import CountryWorldMap from './CountryWorldMap';
import '../responsive.css';

const { Title, Text } = Typography;

// Modern, elegant font styling for charts
const getChartFontStyle = (isDark: boolean) => ({
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  fontSize: '13px',
  fontWeight: 500,
  letterSpacing: '0.3px',
  fill: isDark ? 'rgba(255, 255, 255, 0.65)' : '#4B5563'
});

const getChartLabelStyle = (isDark: boolean) => ({
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  fontSize: '13px',
  fontWeight: 600,
  letterSpacing: '0.2px',
  fill: isDark ? 'rgba(255, 255, 255, 0.85)' : '#1F2937'
});

const getLegendStyle = (isDark: boolean) => ({
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  fontSize: '13px',
  fontWeight: 500,
  letterSpacing: '0.2px',
  color: isDark ? 'rgba(255, 255, 255, 0.65)' : '#374151'
});

// Helper function to format revenue values conditionally
const formatRevenue = (value: number) => {
  if (value === 0) return '$0.0M';
  if (Math.abs(value) < 1.0) {
    return `$${value.toFixed(2)}M`;
  }
  return `$${value.toFixed(1)}M`;
};

// Helper function to format delta values conditionally
const formatDelta = (value: number) => {
  const sign = value >= 0 ? '+' : '';
  if (value === 0) return `${sign}$0.0M`;
  if (Math.abs(value) < 1.0) {
    return `${sign}$${value.toFixed(2)}M`;
  }
  return `${sign}$${value.toFixed(1)}M`;
};

interface CountryInsightsProps {
  data: any;
  loading: boolean;
}

interface CountryData {
  country: string;
  region: string;
  revenue_2025: number;
  revenue_2034: number;
  cagr: number;
  market_share: number;
  rank: number; // Added rank field
  // New fields for the updated structure
  revStart: number;
  revEnd: number;
  shareStart: number;
  shareEnd: number;
  delta: number;
}

interface APICountryData {
  country: string;
  region: string;
  revenue_million: number;
  cagr: number;
  year: number;
}

interface RegionalSummary {
  region: string;
  total_revenue: number;
  country_count: number;
  avg_cagr: number;
}

// Color mapping for countries (unique colors for each country)
const COUNTRY_COLORS: { [key: string]: string } = {
  'USA': '#1890ff',        // Blue
  'China': '#52c41a',      // Green
  'Japan': '#faad14',      // Orange
  'Germany': '#722ed1',    // Purple
  'France': '#13c2c2',     // Teal
  'RoW': '#bfbfbf',        // Light Grey
  'UK': '#fa8c16',         // Dark Orange
  'Italy': '#eb2f96',      // Pink
  'Spain': '#a0d911',      // Lime Green
  'Canada': '#2f54eb',     // Dark Blue
  'Australia': '#fa8c16',  // Gold
  'Brazil': '#f5222d',     // Red
  'India': '#722ed1',      // Purple
  'South Korea': '#13c2c2', // Teal
  'Russia': '#faad14',     // Orange
  'Turkey': '#fa541c',     // Red-Orange
  'Switzerland': '#8c8c8c', // Gray
  'Saudi Arabia': '#faad14', // Gold
  'Portugal': '#eb2f96',   // Pink
  'Poland': '#722ed1',     // Purple
  'Taiwan': '#13c2c2',     // Teal
  'South Africa': '#a0d911', // Lime Green
  'Netherlands': '#52c41a', // Green
  'Belgium': '#1890ff',    // Blue
  'Sweden': '#722ed1',     // Purple
  'Norway': '#13c2c2',     // Teal
  'Denmark': '#faad14',    // Gold
  'Finland': '#a0d911',    // Lime Green
  'Austria': '#eb2f96',    // Pink
  'Greece': '#fa8c16',     // Orange
  'Mexico': '#ff85c0',     // Hot Pink
  'Egypt': '#36cfc9',      // Bright Cyan
  'Others': '#8c8c8c',     // Neutral gray for Others slice
  'Unknown': '#d9d9d9'
};

// Generate unique colors for countries not in our predefined list
const generateUniqueColor = (countryName: string, index: number): string => {
  // Use a hash function to generate consistent colors for the same country name
  let hash = 0;
  for (let i = 0; i < countryName.length; i++) {
    const char = countryName.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  // Generate colors using HSL with better distribution
  const hue = (hash + index * 137.5) % 360; // 137.5° spacing for better distribution
  const saturation = 70 + (hash % 20); // 70-90% saturation for vibrant colors
  const lightness = 40 + (hash % 25); // 40-65% lightness for good contrast
  
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

// Function to get color for a country, with fallback to generated colors
const getCountryColor = (countryName: string, index: number): string => {
  if (COUNTRY_COLORS[countryName]) {
    return COUNTRY_COLORS[countryName];
  }
  
  // Generate a unique color for unknown countries
  return generateUniqueColor(countryName, index);
};

// Color mapping for regions (keeping for other charts)
const REGION_COLORS: { [key: string]: string } = {
  'North America': '#1890ff',
  'Western Europe': '#52c41a',
  'Asia Pacific': '#faad14',
  'Eastern Europe': '#722ed1',
  'Middle East & Africa': '#f5222d',
  'Latin & Central America': '#13c2c2',
  'Unknown': '#fa8c16'
};

const CountryInsights: React.FC<CountryInsightsProps> = ({ data, loading }) => {
  const { isDark } = useTheme();
  const [countryData, setCountryData] = useState<CountryData[]>([]);
  const [regionalSummary, setRegionalSummary] = useState<RegionalSummary[]>([]);
  const [topCountries, setTopCountries] = useState<CountryData[]>([]);
  const [chartLoading, setChartLoading] = useState(false);
  const [fullscreenChart, setFullscreenChart] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const { filters, updateFilter } = useFilters();

  // Custom tooltip styling
  const CustomTooltip = ({ active, payload, isDark }: any) => {
    if (active && payload && payload.length) {
      const entry = payload[0].payload;
      const country = entry.country || payload[0].name;
      const value = payload[0].value;
      
      // Handle "Others" case
      if (country === 'Others') {
        const totalRepresented = entry.metadata?.totalRepresented || 0;
        const othersText = filters.countries.length > 0 && filters.countries.length < 27
          ? `$${Number(value).toFixed(1)}M (${totalRepresented} countries)`
          : `$${Number(value).toFixed(1)}M`;
        
        return (
          <div style={{
            backgroundColor: isDark ? '#1f1f1f' : 'rgba(255, 255, 255, 0.98)',
            border: `1px solid ${isDark ? '#434343' : '#e5e7eb'}`,
            borderRadius: '8px',
            padding: '12px 16px',
            boxShadow: isDark ? '0 4px 6px -1px rgba(0, 0, 0, 0.5), 0 2px 4px -1px rgba(0, 0, 0, 0.3)' : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            fontSize: '14px',
            fontWeight: 500
          }}>
            <div style={{ color: isDark ? 'rgba(255, 255, 255, 0.65)' : '#6b7280', marginBottom: '4px' }}>Others</div>
            <div style={{ color: isDark ? 'rgba(255, 255, 255, 0.85)' : '#111827', fontSize: '16px', fontWeight: 600 }}>
              {othersText}
            </div>
          </div>
        );
      }
      
      return (
        <div style={{
          backgroundColor: isDark ? '#1f1f1f' : 'rgba(255, 255, 255, 0.98)',
          border: `1px solid ${isDark ? '#434343' : '#e5e7eb'}`,
          borderRadius: '8px',
          padding: '12px 16px',
          boxShadow: isDark ? '0 4px 6px -1px rgba(0, 0, 0, 0.5), 0 2px 4px -1px rgba(0, 0, 0, 0.3)' : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          fontSize: '14px',
          fontWeight: 500
        }}>
          <div style={{ color: isDark ? 'rgba(255, 255, 255, 0.65)' : '#6b7280', marginBottom: '4px' }}>{country}</div>
          <div style={{ color: isDark ? 'rgba(255, 255, 255, 0.85)' : '#111827', fontSize: '16px', fontWeight: 600 }}>
            ${Number(value).toFixed(1)}M
          </div>
          {entry.market_share && (
            <div style={{ color: isDark ? 'rgba(255, 255, 255, 0.65)' : '#6b7280', fontSize: '12px', marginTop: '4px' }}>
              {entry.market_share.toFixed(1)}% market share
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  // Defensive check: ensure yearRange is an array
  const safeYearRange = Array.isArray(filters.yearRange) ? filters.yearRange : [2019, 2034];
  
  // Determine which year to show for revenue and market share
  // For default full range, show a representative year, otherwise use the actual range
  const isFullRange = (filters.therapeuticArea === 'Skin Cancer' && safeYearRange[0] === 2019 && safeYearRange[1] === 2034) ||
                      (filters.therapeuticArea === 'Psoriasis' && safeYearRange[0] === 2020 && safeYearRange[1] === 2035) ||
                      (filters.therapeuticArea === 'Skin Cancer - Trial Version' && safeYearRange[0] === 2019 && safeYearRange[1] === 2025);
  const displayYear = isFullRange ? 
    (filters.therapeuticArea === 'Psoriasis' ? 2030 : 
     filters.therapeuticArea === 'Skin Cancer - Trial Version' ? 2025 : 2025) : 
    safeYearRange[1];
  const startYearDisplay = isFullRange ? 
    (filters.therapeuticArea === 'Psoriasis' ? 2025 : 
     filters.therapeuticArea === 'Skin Cancer - Trial Version' ? 2019 : 2025) : 
    safeYearRange[0];

  // Process pie chart data to show top 5 countries + Others by default, or more when filtered
  // Default view: Top 5 countries by revenue + Others (remaining countries)
  // Filtered view: All selected countries (up to 10) + Others if more than 10 selected
  const getPieChartData = (countries: CountryData[]) => {
    if (!countries || !Array.isArray(countries) || countries.length === 0) return [];
    
    console.log('getPieChartData called with:', {
      totalCountries: countries.length,
      filtersCountries: filters.countries.length,
      sampleCountries: countries.slice(0, 3).map(c => ({ country: c.country, revenue: c.revEnd })),
      allCountries: countries.map(c => ({ country: c.country, revenue: c.revEnd })),
      totalRevenue: countries.reduce((sum, c) => sum + c.revEnd, 0),
      expectedOthers: filters.countries.length > 10 ? 'Should show Others' : 'No Others needed',
      actualDataCount: countries.length,
      filterSelectionCount: filters.countries.length
    });
    
    // Check if user has explicitly applied a country filter (not just default "all countries")
    const hasExplicitCountryFilter = filters.countries.length > 0 && filters.countries.length < 27; // Assuming 27 is total available countries
    
    if (hasExplicitCountryFilter) {
      // If 10 or fewer countries selected, show all of them
      if (filters.countries.length <= 10) {
        console.log('Showing all filtered countries:', filters.countries.length);
        return countries;
      } else {
        // If more than 10 countries selected, group into top 10 + Others
        console.log('Grouping filtered countries into top 10 + Others');
        console.log('Expected countries from filter:', filters.countries.length);
        console.log('Actual countries received from API:', countries.length);
        
        // First, calculate total revenue for all countries
        const totalRevenue = countries.reduce((sum, country) => sum + country.revEnd, 0);
        
        // Sort all countries by revenue
        const sortedCountries = [...countries].sort((a, b) => b.revEnd - a.revEnd);
        
        // Split into top 10 and remaining
        const top10 = sortedCountries.slice(0, 10);
        const remainingCountries = sortedCountries.slice(10);
        
        // Calculate Others revenue - sum of ALL remaining countries
        const othersRevenue = remainingCountries.reduce((sum, country) => sum + country.revEnd, 0);
        
        console.log('Filtered Others calculation:', {
          totalCountries: countries.length,
          top10Revenue: top10.reduce((sum, c) => sum + c.revEnd, 0),
          othersRevenue,
          totalRevenue,
          remainingCountries: remainingCountries.map(c => ({ country: c.country, revenue: c.revEnd })),
          remainingCount: remainingCountries.length,
          top10Countries: top10.map(c => c.country),
          allCountries: countries.map(c => ({ country: c.country, revenue: c.revEnd })),
          revenueCheck: {
            totalRevenueSum: totalRevenue,
            top10RevenueSum: top10.reduce((sum, c) => sum + c.revEnd, 0),
            othersRevenueSum: othersRevenue,
            verifySum: top10.reduce((sum, c) => sum + c.revEnd, 0) + othersRevenue === totalRevenue
          },
          debugInfo: {
            expectedOthersFromFilter: filters.countries.length - 10,
            actualRemainingCountries: remainingCountries.length,
            sortedByRevenue: sortedCountries.map(c => ({ country: c.country, revenue: c.revEnd })),
            othersCalculationDetails: {
              step1_allCountries: countries.length,
              step2_sorted: sortedCountries.length,
              step3_top10: top10.length,
              step4_remaining: remainingCountries.length,
              step5_othersRevenue: othersRevenue
            }
          }
        });
        
        // Create Others entry
        const othersEntry = {
          country: 'Others',
          region: 'Multiple',
          revenue_2025: othersRevenue, // Keep for backward compatibility
          revenue_2034: othersRevenue * 1.44,
          cagr: 0,
          market_share: totalRevenue > 0 ? (othersRevenue / totalRevenue) * 100 : 0,
          rank: 11,
          revStart: 0, // Others doesn't have start year data
          revEnd: othersRevenue,
          shareStart: 0,
          shareEnd: totalRevenue > 0 ? (othersRevenue / totalRevenue) * 100 : 0,
          delta: othersRevenue,
          metadata: {
            remainingCountries: remainingCountries.map(c => ({ country: c.country, revenue: c.revEnd })),
            totalRepresented: remainingCountries.length
          }
        };
        
        // Calculate market share for top 10 countries
        const top10WithMarketShare = top10.map(country => ({
          ...country,
          market_share: totalRevenue > 0 ? (country.revEnd / totalRevenue) * 100 : 0
        }));
        
        const result = [...top10WithMarketShare, othersEntry];
        console.log('Filtered result with Others:', {
          totalSlices: result.length,
          othersRevenue,
          remainingCount: remainingCountries.length,
          verifyRevenue: {
            totalRevenue,
            sumOfAllSlices: result.reduce((sum, slice) => sum + slice.revenue_2025, 0),
            matches: Math.abs(totalRevenue - result.reduce((sum, slice) => sum + slice.revenue_2025, 0)) < 0.01
          }
        });
        return result;
      }
    }
    
    // Default view: show top 8 countries + Others (excluding RoW from top 8)
    console.log('Default view: showing top 8 + Others (RoW included in Others)');
    const sortedCountries = [...countries].sort((a, b) => b.revEnd - a.revEnd);
    
    // In default view, exclude RoW from top 8 and add it to Others
    const top8 = sortedCountries.filter(country => country.country !== 'RoW').slice(0, 8);
    const remaining = sortedCountries.filter(country => country.country !== 'RoW').slice(8);
    
    // Find RoW data to add to Others
    const rowData = sortedCountries.find(country => country.country === 'RoW');
    
    // If we have more than 8 countries (excluding RoW), group the rest + RoW into "Others"
    if (remaining.length > 0 || rowData) {
      const othersRevenue = remaining.reduce((sum, country) => sum + country.revEnd, 0) + (rowData ? rowData.revEnd : 0);
      const totalRevenue = countries.reduce((sum, country) => sum + country.revEnd, 0);
      const othersPercentage = totalRevenue > 0 ? (othersRevenue / totalRevenue) * 100 : 0;
      
      console.log('Others calculation (RoW included):', {
        totalCountries: countries.length,
        top8Revenue: top8.reduce((sum, c) => sum + c.revEnd, 0),
        othersRevenue,
        totalRevenue,
        othersPercentage,
        remainingCountries: remaining.map(c => ({ country: c.country, revenue: c.revEnd })),
        rowIncluded: !!rowData,
        rowRevenue: rowData ? rowData.revEnd : 0
      });
      
      const othersEntry = {
        country: 'Others',
        region: 'Multiple',
        revenue_2025: othersRevenue, // Keep for backward compatibility
        revenue_2034: othersRevenue * 1.44,
        cagr: 0,
        market_share: othersPercentage,
        rank: 9,
        revStart: 0, // Others doesn't have start year data
        revEnd: othersRevenue,
        shareStart: 0,
        shareEnd: othersPercentage,
        delta: othersRevenue
      };
      
      // Recalculate percentages for top 8 countries after grouping
      const top8WithRecalculatedPercentages = top8.map(country => ({
        ...country,
        market_share: totalRevenue > 0 ? (country.revEnd / totalRevenue) * 100 : 0
      }));
      
      const result = [...top8WithRecalculatedPercentages, othersEntry];
      console.log('Default result (RoW in Others):', result.length, 'slices, Others revenue:', othersRevenue);
      return result;
    }
    
    return top8;
  };

  const fetchCountryData = async () => {
    try {
      // Don't fetch if therapeutic area is not set
      if (!filters.therapeuticArea) {
        console.log('Skipping fetch: therapeutic area not set');
        return;
      }

      setChartLoading(true);
      
      // Get the correct year range for the current therapeutic area
      const getCorrectYearRange = (): [number, number] => {
        // Dermatology domain
        if (filters.therapeuticArea === 'Dermatology - In Sight' || filters.therapeuticArea === 'Dermatology') {
          return [2020, 2035];
        } else if (filters.therapeuticArea === 'Dermatology Pipeline') {
          return [2020, 2035];
        } else if (filters.therapeuticArea === 'Psoriasis - Focus' || filters.therapeuticArea === 'Psoriasis') {
          return [2020, 2035];
        } else if (filters.therapeuticArea === 'Skin Cancer - Trial Version') {
          return [2019, 2025];
        } else if (filters.therapeuticArea === 'Skin Cancer - Focus' || filters.therapeuticArea === 'Skin Cancer') {
          return [2019, 2034];
        // Ophthalmology domain
        } else if (filters.therapeuticArea === 'Ophthalmology - In Sight' || filters.therapeuticArea === 'Ophthalmology') {
          return [2021, 2036];
        } else if (filters.therapeuticArea === 'Retinal - Focus' || filters.therapeuticArea === 'Retinal Disorders') {
          return [2021, 2036];
        } else if (filters.therapeuticArea === 'Glaucoma - Focus' || filters.therapeuticArea === 'Glaucoma') {
          return [2021, 2036];
        // Cell, Gene & RNA domain
        } else if (filters.therapeuticArea === 'Cell, Gene & RNA Therapies - In Sight' || filters.therapeuticArea === 'Cell, Gene & RNA Therapies') {
          return [2020, 2036];
        } else {
          return [2019, 2025]; // Default to trial version range
        }
      };

      const correctYearRange = getCorrectYearRange();
      const safeYearRange = Array.isArray(filters.yearRange) && filters.yearRange.length === 2
        ? filters.yearRange
        : correctYearRange;
      
      // Validate that the year range is within the correct bounds for this therapeutic area
      const [minYear, maxYear] = correctYearRange;
      let [startYear, endYear] = safeYearRange;
      
      // If the range is invalid for this therapeutic area, use the correct range
      if (startYear < minYear || startYear > maxYear || endYear < minYear || endYear > maxYear || startYear > endYear) {
        console.warn('Year range invalid for therapeutic area, updating to correct range:', {
          therapeuticArea: filters.therapeuticArea,
          providedRange: safeYearRange,
          correctRange: correctYearRange
        });
        // Update the filter context to use the correct range
        updateFilter('yearRange', correctYearRange);
        // Use the correct range for this fetch
        [startYear, endYear] = correctYearRange;
      }
      
      // Fetch data for the entire year range
      const params = new URLSearchParams({
        start_year: startYear.toString(),
        end_year: endYear.toString()
      });

      // Add therapeutic area parameter
      if (filters.therapeuticArea && filters.therapeuticArea !== 'All') {
        params.append('therapeutic_area', filters.therapeuticArea);
      }

      // Add country filter if countries are selected
      if (filters.countries.length > 0) {
        params.append('countries', filters.countries.join(','));
      }

      console.log('Fetching country data for years:', {
        startYear,
        endYear,
        countries: filters.countries
      });

      const response = await api.get(`/api/market/countries?${params}`);
      const allData = (response.data || []) as APICountryData[];

      // Split data into start and end year
      const startYearData = allData.filter((d: APICountryData) => d.year === startYear);
      const endYearData = allData.filter((d: APICountryData) => d.year === endYear);
      
      console.log('API Response:', {
        totalDataLength: allData.length,
        startYearDataLength: startYearData.length,
        endYearDataLength: endYearData.length,
        sampleStartData: startYearData[0],
        sampleEndData: endYearData[0],
        yearRange: filters.yearRange,
        therapeuticArea: filters.therapeuticArea,
        apiUrl: `/api/market/countries?${params}`
      });

      if (startYearData.length > 0 || endYearData.length > 0) {
        console.log('Processing API data:', {
          startYearItems: startYearData.length,
          endYearItems: endYearData.length,
          sampleStartItems: startYearData.slice(0, 3).map((item: any) => ({
            country: item.country,
            region: item.region,
            revenue: item.revenue_million,
            year: item.year
          })),
          sampleEndItems: endYearData.slice(0, 3).map((item: any) => ({
            country: item.country,
            region: item.region,
            revenue: item.revenue_million,
            year: item.year
          }))
        });
        
        // Group by country and get both start and end year data
        const countryData = new Map<string, {
          country: string;
          region: string;
          startYearRevenue: number;
          endYearRevenue: number;
        }>();

        // Process start year data
        startYearData.forEach((item: APICountryData) => {
          const country = item.country;
          const region = item.region || 'Unknown';
          const revenue = item.revenue_million || 0;

          if (!countryData.has(country)) {
            countryData.set(country, {
              country,
              region,
              startYearRevenue: 0,
              endYearRevenue: 0
            });
          }

          countryData.get(country)!.startYearRevenue = revenue;
        });

        // Process end year data
        endYearData.forEach((item: APICountryData) => {
          const country = item.country;
          const region = item.region || 'Unknown';
          const revenue = item.revenue_million || 0;

          if (!countryData.has(country)) {
            countryData.set(country, {
              country,
              region,
              startYearRevenue: 0,
              endYearRevenue: 0
            });
          }

          countryData.get(country)!.endYearRevenue = revenue;
        });

        console.log('Country data processing:', {
          totalCountries: countryData.size,
          yearRange: filters.yearRange,
          startYear,
          endYear,
          sampleCountry: Array.from(countryData.entries())[0]
        });

        // Calculate totals for both years
        const totalStart = Array.from(countryData.values())
          .reduce((sum, item) => sum + item.startYearRevenue, 0);
        const totalEnd = Array.from(countryData.values())
          .reduce((sum, item) => sum + item.endYearRevenue, 0);
        
        console.log('Revenue calculation:', {
          totalStart,
          totalEnd,
          countriesWithStartRevenue: Array.from(countryData.values()).filter(item => item.startYearRevenue > 0).length,
          countriesWithEndRevenue: Array.from(countryData.values()).filter(item => item.endYearRevenue > 0).length,
          sampleRevenues: Array.from(countryData.values()).slice(0, 3).map(item => ({
            country: item.country,
            startYear: item.startYearRevenue,
            endYear: item.endYearRevenue
          }))
        });
        
        const processedData: CountryData[] = Array.from(countryData.values())
          .map((item, index) => {
            const revStart = item.startYearRevenue;
            const revEnd = item.endYearRevenue;
            const shareStart = totalStart > 0 ? (revStart / totalStart) * 100 : 0;
            const shareEnd = totalEnd > 0 ? (revEnd / totalEnd) * 100 : 0;
            const delta = revEnd - revStart;
            
            // Calculate CAGR over the selected range
            let cagr = 0;
            if (revStart > 0 && revEnd > 0 && endYear !== startYear) {
              cagr = (Math.pow(revEnd / revStart, 1 / (endYear - startYear)) - 1) * 100;
            }
            // If years are equal or revStart <= 0, CAGR shows as "–"
            
            return {
              country: item.country,
              region: item.region,
              revenue_2025: revEnd, // Keep for backward compatibility
              revenue_2034: revEnd * 1.44, // Approximate 2034 projection
              cagr: cagr,
              market_share: shareEnd, // Keep for backward compatibility
              rank: index + 1,
              // New fields
              revStart,
              revEnd,
              shareStart,
              shareEnd,
              delta
            };
          });

        console.log('Data processing summary:', {
          totalCountriesBeforeFilter: countryData.size,
          countriesWithZeroRevenue: Array.from(countryData.values()).filter(item => item.endYearRevenue === 0).length,
          countriesWithRevenue: processedData.length,
          sampleProcessed: processedData.slice(0, 3).map(item => ({
            country: item.country,
            revStart: item.revStart,
            revEnd: item.revEnd,
            delta: item.delta,
            cagr: item.cagr
          })),
          zeroRevenueCountries: Array.from(countryData.values())
            .filter(item => item.endYearRevenue === 0)
            .map(item => ({ country: item.country, revenue: item.endYearRevenue })),
          allProcessedCountries: processedData.map(item => ({
            country: item.country,
            revStart: item.revStart,
            revEnd: item.revEnd
          })),
          filterVsProcessed: {
            selectedCountries: filters.countries,
            processedCountries: processedData.map(item => item.country),
            missingFromProcessed: filters.countries.filter(country => 
              !processedData.map(item => item.country).includes(country)
            )
          }
        });

        setCountryData(processedData);
        
        // Get top countries by revenue - show ALL filtered countries when filters are applied
        const maxCountries = filters.countries.length > 0 ? filters.countries.length : 1000;
        const sortedCountries = [...processedData].sort((a, b) => {
          // Place RoW at the end of the list
          if (a.country === 'RoW' && b.country !== 'RoW') return 1;
          if (b.country === 'RoW' && a.country !== 'RoW') return -1;
          // For all other countries, sort by revenue (descending)
          return b.revEnd - a.revEnd;
        });
        const topCountriesData = sortedCountries.slice(0, maxCountries).map((item, index) => ({
          ...item,
          rank: index + 1 // Update rank after sorting
        }));
        
        console.log('Final processed data for pie chart:', {
          totalProcessed: processedData.length,
          topCountriesCount: topCountriesData.length,
          sampleTopCountries: topCountriesData.slice(0, 3).map(c => ({ country: c.country, revEnd: c.revEnd })),
          filtersApplied: filters.countries.length > 0,
          selectedCountries: filters.countries,
          dataCountries: processedData.map(c => c.country),
          revenueSummary: {
            totalRevenue: processedData.reduce((sum, c) => sum + c.revEnd, 0),
            countriesWithRevenue: processedData.filter(c => c.revEnd > 0).length,
            countriesWithZeroRevenue: processedData.filter(c => c.revEnd === 0).length
          }
        });
        
        setTopCountries(topCountriesData);

        // Calculate regional summaries based on end-year data
        const regionMap = new Map<string, RegionalSummary>();
        processedData.forEach(country => {
          const existing = regionMap.get(country.region);
          if (existing) {
            existing.total_revenue += country.revEnd; // Use revEnd for end-year revenue
            existing.country_count += 1;
            existing.avg_cagr = (existing.avg_cagr + country.cagr) / 2;
          } else {
            regionMap.set(country.region, {
              region: country.region,
              total_revenue: country.revEnd, // Use revEnd for end-year revenue
              country_count: 1,
              avg_cagr: country.cagr
            });
          }
        });

        const regionalData = Array.from(regionMap.values())
          .filter(region => region.total_revenue > 0) // Filter out regions with zero revenue
          .sort((a, b) => b.total_revenue - a.total_revenue);
        
        setRegionalSummary(regionalData);
        
        // Check if all selected countries are present in the API response
        if (filters.countries.length > 0) {
          const apiCountries = Array.from(countryData.keys());
          const missingCountries = filters.countries.filter(country => !apiCountries.includes(country));
          const extraCountries = apiCountries.filter(country => !filters.countries.includes(country));
          
          console.log('Country filter validation:', {
            selectedCountries: filters.countries,
            apiCountries,
            missingCountries,
            extraCountries,
            allSelectedPresent: missingCountries.length === 0,
            selectedCount: filters.countries.length,
            apiCount: apiCountries.length,
            willShowOthers: filters.countries.length > 10 && apiCountries.length > 10,
            missingRevenue: missingCountries.length > 0 ? 'Some selected countries missing from API response' : 'All selected countries present',
            dataProcessingIssue: filters.countries.length > apiCountries.length ? 'API returned fewer countries than selected' : 'API returned expected or more countries'
          });
          
          // If we have missing countries, this could explain why Others shows $0M
          if (missingCountries.length > 0) {
            console.warn('WARNING: Some selected countries are missing from API response:', missingCountries);
            console.warn('This explains why Others shows $0M - no data for these countries');
          }
          
          // If API returned fewer countries than selected, log the difference
          if (filters.countries.length > apiCountries.length) {
            console.warn('WARNING: API returned fewer countries than selected:', {
              selected: filters.countries.length,
              returned: apiCountries.length,
              difference: filters.countries.length - apiCountries.length
            });
          }
        }

      } else {
        setCountryData([]);
        setTopCountries([]);
        setRegionalSummary([]);
      }
    } catch (error) {
      console.error('Error fetching country data:', error);
      // Set fallback data
      setCountryData([]);
      setTopCountries([]);
      setRegionalSummary([]);
    } finally {
      setChartLoading(false);
    }
  };

  useEffect(() => {
    fetchCountryData();
    setCurrentPage(1); // Reset to first page when filters change
  }, [filters.countries, filters.yearRange, filters.therapeuticArea]);

  // Use the safeYearRange already declared at the top of the component
  const [startYear, endYear] = safeYearRange;

  // Helper function to get top 8 countries for bar chart (excluding Others)
  const getTop8CountriesData = useMemo(() => {
    const pieData = getPieChartData(topCountries);
    // Filter out "Others" and limit to top 8
    return pieData.filter(item => item.country !== 'Others').slice(0, 8);
  }, [topCountries, filters.countries.length]);

  const countryColumns = [
    {
      title: '#',
      dataIndex: 'rank',
      key: 'rank',
      width: 50,
      render: (rank: number) => rank,
    },
    {
      title: 'Country',
      dataIndex: 'country',
      key: 'country',
      render: (text: string, record: CountryData) => (
        <div>
          <Text strong>{text}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.region}
          </Text>
        </div>
      ),
    },
    {
      title: `Revenue (${startYear})`,
      dataIndex: 'revStart',
      key: 'revStart',
      render: (value: number) => formatRevenue(value),
      sorter: (a: CountryData, b: CountryData) => a.revStart - b.revStart,
    },
    {
      title: `Market Share (${startYear})`,
      dataIndex: 'shareStart',
      key: 'shareStart',
      render: (value: number) => `${value.toFixed(1)}%`,
      sorter: (a: CountryData, b: CountryData) => a.shareStart - b.shareStart,
    },
    {
      title: `Revenue (${endYear})`,
      dataIndex: 'revEnd',
      key: 'revEnd',
      defaultSortOrder: 'descend' as const,
      render: (value: number) => formatRevenue(value),
      sorter: (a: CountryData, b: CountryData) => a.revEnd - b.revEnd,
    },
    {
      title: `Market Share (${endYear})`,
      dataIndex: 'shareEnd',
      key: 'shareEnd',
      render: (value: number) => `${value.toFixed(1)}%`,
      sorter: (a: CountryData, b: CountryData) => a.shareEnd - b.shareEnd,
    },
    {
      title: `Δ$ (${startYear}→${endYear})`,
      dataIndex: 'delta',
      key: 'delta',
      render: (value: number) => (
        <span style={{ color: value >= 0 ? '#52c41a' : '#f5222d' }}>
          {formatDelta(value)}
        </span>
      ),
      sorter: (a: CountryData, b: CountryData) => a.delta - b.delta,
    },
    {
      title: `CAGR (${startYear}–${endYear})`,
      dataIndex: 'cagr',
      key: 'cagr',
      render: (value: number) => {
        if (startYear === endYear || value === 0) {
          return '–';
        }
        return (
          <span style={{ color: '#52c41a' }}>
            {value.toFixed(1)}%
          </span>
        );
      },
      sorter: (a: CountryData, b: CountryData) => a.cagr - b.cagr,
    },
  ];

  if (loading || chartLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>
          <Text>Loading country insights...</Text>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Row gutter={[16, 16]}>
        {/* Country Market Distribution Chart */}
        <Col xs={24} lg={12}>
          <Card 
            title={
              <div style={{ margin: 0, padding: 0 }}>
                <Title level={5} style={{ margin: 0, marginBottom: 0 }}>
                  Market Distribution by Country
                </Title>
                <Text type="secondary" style={{ fontSize: '12px', margin: 0, display: 'block' }}>
                  {filters.countries.length > 0 && filters.countries.length < 27 ? 
                    (filters.countries.length <= 10 ? 
                      `Selected ${filters.countries.length} countries` : 
                      `Top 10 selected + Others (${filters.countries.length - 10} remaining)`
                    ) : 
                    'Top 8 countries + Others by revenue'
                  }
                </Text>
              </div>
            }
            loading={chartLoading}
            style={{ height: 460, display: 'flex', flexDirection: 'column' }}
            headStyle={{ padding: '12px 20px', minHeight: '72px', height: '72px' }}
            bodyStyle={{ padding: '12px 20px', flex: 1 }}
          >
            {topCountries.length > 0 ? (
              <FullscreenChart
                chartId="country-distribution"
                title="Country Market Distribution"
                isFullscreen={fullscreenChart === 'country-distribution'}
                onToggleFullscreen={() => setFullscreenChart(fullscreenChart === 'country-distribution' ? null : 'country-distribution')}
                fullscreenContent={
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={getPieChartData(topCountries)}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ country, revEnd, market_share, cx, cy, midAngle, outerRadius }) => {
                          if (country === 'Others') {
                            if (typeof midAngle === 'undefined' || typeof outerRadius === 'undefined' || typeof cx === 'undefined' || typeof cy === 'undefined') {
                              return null;
                            }
                            const RADIAN = Math.PI / 180;
                            const radius = outerRadius + 18;
                            const x = cx + radius * Math.cos(-midAngle * RADIAN);
                            const y = cy + radius * Math.sin(-midAngle * RADIAN);
                            const othersColor = getCountryColor('Others', 0);
                            return (
                              <text 
                                x={x} 
                                y={y} 
                                dy={4} 
                                textAnchor={x > cx ? 'start' : 'end'} 
                                dominantBaseline="central"
                                fill={othersColor}
                                style={{
                                  fontFamily: getChartLabelStyle(isDark).fontFamily,
                                  fontSize: getChartLabelStyle(isDark).fontSize,
                                  fontWeight: getChartLabelStyle(isDark).fontWeight,
                                  letterSpacing: getChartLabelStyle(isDark).letterSpacing
                                }}
                              >
                                {`Others: $${revEnd.toFixed(0)}M`}
                              </text>
                            );
                          }

                          // Calculate base label position
                          const RADIAN = Math.PI / 180;
                          const baseRadius = (outerRadius || 0) + 18;
                          let x = cx + baseRadius * Math.cos(-(midAngle || 0) * RADIAN);
                          let y = cy + baseRadius * Math.sin(-(midAngle || 0) * RADIAN);
                          
                          // Adjust positions for better spacing in default view
                          if (filters.countries.length === 0 || filters.countries.length >= 27) {
                            if (filters.therapeuticArea === 'Psoriasis' || filters.therapeuticArea === 'Psoriasis - Focus') {
                              // Psoriasis-specific adjustments for label positioning
                              if (country === 'Germany') {
                                y -= 10;
                                x += 10;
                              } else if (country === 'France') {
                                y += 5;
                              } else if (country === 'Canada') {
                                y += 15;
                                x -= 5;
                              } else if (country === 'India') {
                                y += 25;
                                x += 25;
                              }
                            } else if (filters.therapeuticArea === 'Dermatology' || filters.therapeuticArea === 'Dermatology - In Sight') {
                              // Dermatology-specific adjustments for label positioning
                              // Increase base radius for more spacing
                              const dermatologyRadius = (outerRadius || 0) + 25;
                              x = cx + dermatologyRadius * Math.cos(-(midAngle || 0) * RADIAN);
                              y = cy + dermatologyRadius * Math.sin(-(midAngle || 0) * RADIAN);
                              
                              // Country-specific adjustments to prevent overlapping
                              if (country === 'USA') {
                                y -= 5;
                              } else if (country === 'China') {
                                x += 5;
                                y -= 3;
                              } else if (country === 'Japan') {
                                x += 8;
                                y += 5;
                              } else if (country === 'Germany') {
                                x -= 5;
                                y += 8;
                              } else if (country === 'UK') {
                                x -= 8;
                                y += 12;
                              } else if (country === 'France') {
                                x -= 12;
                                y += 18; // Increased spacing from UK
                              } else if (country === 'Canada') {
                                x += 10;
                                y += 24; // Increased spacing from France
                              } else if (country === 'Brazil') {
                                x += 15;
                                y += 30; // Increased spacing from Canada
                              }
                            } else {
                              // Existing adjustments for Skin Cancer
                              if (country === 'UK') {
                                x += 8; // Move UK label slightly right (adjusted for larger radius)
                              } else if (country === 'Australia') {
                                y += 15; // Move Australia label down more (adjusted for larger radius)
                                x -= 10; // Move Australia label towards Australia's slice
                              } else if (country === 'Italy') {
                                x += 33; // Move Italy label further to the right (towards Italy's slice)
                                y += 5; // Move Italy label down
                              }
                            }
                          }
                          
                          const labelText = `${country}: $${revEnd.toFixed(0)}M`;
                          
                          return (
                            <text 
                              x={x} 
                              y={y} 
                              dy={4} 
                              textAnchor={x > cx ? 'start' : 'end'} 
                              fill={getCountryColor(country, 0)} 
                              style={{
                                fontFamily: getChartLabelStyle(isDark).fontFamily,
                                fontSize: getChartLabelStyle(isDark).fontSize,
                                fontWeight: getChartLabelStyle(isDark).fontWeight,
                                letterSpacing: getChartLabelStyle(isDark).letterSpacing
                              }}
                            >
                              {labelText}
                            </text>
                          );
                        }}
                        innerRadius={60}
                        outerRadius={120}
                        fill="#8884d8"
                        dataKey="revEnd"
                        paddingAngle={2}
                        animationBegin={0}
                        animationDuration={800}
                        animationEasing="ease-out"
                      >
                        {getPieChartData(topCountries).map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={getCountryColor(entry.country, index)}
                            stroke="#fff"
                            strokeWidth={2}
                            style={{
                              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                              transition: 'opacity 0.3s'
                            }}
                          />
                        ))}
                      </Pie>
                      <RechartsTooltip content={<CustomTooltip isDark={isDark} />} />
                      <Legend 
                        wrapperStyle={getLegendStyle(isDark)}
                        formatter={(value: any, entry: any) => [entry.payload.country]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                }
              >
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={getPieChartData(topCountries)}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ country, revEnd, market_share, cx, cy, midAngle, outerRadius }) => {
                        if (country === 'Others') {
                          if (typeof midAngle === 'undefined' || typeof outerRadius === 'undefined' || typeof cx === 'undefined' || typeof cy === 'undefined') {
                            return null;
                          }
                          const RADIAN = Math.PI / 180;
                          const radius = outerRadius + 18;
                          const x = cx + radius * Math.cos(-midAngle * RADIAN);
                          const y = cy + radius * Math.sin(-midAngle * RADIAN);
                          const othersColor = getCountryColor('Others', 0);
                          return (
                            <text 
                              x={x} 
                              y={y} 
                              dy={4} 
                              textAnchor={x > cx ? 'start' : 'end'} 
                              dominantBaseline="central"
                              fill={othersColor}
                              style={{
                                fontFamily: getChartLabelStyle(isDark).fontFamily,
                                fontSize: getChartLabelStyle(isDark).fontSize,
                                fontWeight: getChartLabelStyle(isDark).fontWeight,
                                letterSpacing: getChartLabelStyle(isDark).letterSpacing
                              }}
                            >
                              {`Others: $${revEnd.toFixed(0)}M`}
                            </text>
                          );
                        }

                        // Calculate base label position
                        const RADIAN = Math.PI / 180;
                        const baseRadius = (outerRadius || 0) + 18;
                        let x = cx + baseRadius * Math.cos(-(midAngle || 0) * RADIAN);
                        let y = cy + baseRadius * Math.sin(-(midAngle || 0) * RADIAN);
                        
                        // Adjust positions for better spacing in default view
                        if (filters.countries.length === 0 || filters.countries.length >= 27) {
                          if (filters.therapeuticArea === 'Psoriasis' || filters.therapeuticArea === 'Psoriasis - Focus') {
                              // Psoriasis-specific adjustments for label positioning
                              if (country === 'Germany') {
                                  y -= 8;
                                  x += 5;
                              } else if (country === 'France') {
                                  y += 5;
                                  x -= 2;
                              } else if (country === 'Canada') {
                                  y += 12;
                                  x -= 4;
                              } else if (country === 'India') {
                                  y += 20;
                                  x += 18;
                              }
                          } else if (filters.therapeuticArea === 'Dermatology' || filters.therapeuticArea === 'Dermatology - In Sight') {
                              // Dermatology-specific adjustments for label positioning
                              // Increase base radius for more spacing
                              const dermatologyRadius = (outerRadius || 0) + 20;
                              x = cx + dermatologyRadius * Math.cos(-(midAngle || 0) * RADIAN);
                              y = cy + dermatologyRadius * Math.sin(-(midAngle || 0) * RADIAN);
                              
                              // Country-specific adjustments to prevent overlapping
                              if (country === 'USA') {
                                y -= 3;
                              } else if (country === 'China') {
                                x += 3;
                                y -= 2;
                              } else if (country === 'Japan') {
                                x += 5;
                                y += 3;
                              } else if (country === 'Germany') {
                                x -= 3;
                                y += 5;
                              } else if (country === 'UK') {
                                x -= 5;
                                y += 8;
                              } else if (country === 'France') {
                                x -= 8;
                                y += 13; // Increased spacing from UK
                              } else if (country === 'Canada') {
                                x += 6;
                                y += 18; // Increased spacing from France
                              } else if (country === 'Brazil') {
                                x += 10;
                                y += 23; // Increased spacing from Canada
                              }
                          } else {
                              // Existing adjustments for Skin Cancer
                              if (country === 'UK') {
                                x += 5; // Move UK label slightly right
                              } else if (country === 'Australia') {
                                y += 2; // Move Australia label slightly up
                                x -= 3; // Move Australia label towards Australia's slice
                                                          } else if (country === 'Italy') {
                                  x += 24; // Move Italy label further to the right (towards Italy's slice)
                                  y += 5; // Move Italy label down
                                }
                          }
                        }
                        
                        const labelText = `${country}: $${revEnd.toFixed(0)}M`;
                        
                        return (
                          <text 
                            x={x} 
                            y={y} 
                            dy={4} 
                            textAnchor={x > cx ? 'start' : 'end'} 
                            fill={getCountryColor(country, 0)} 
                            style={{
                              fontFamily: getChartLabelStyle(isDark).fontFamily,
                              fontSize: getChartLabelStyle(isDark).fontSize,
                              fontWeight: getChartLabelStyle(isDark).fontWeight,
                              letterSpacing: getChartLabelStyle(isDark).letterSpacing
                            }}
                          >
                            {labelText}
                          </text>
                        );
                      }}
                      innerRadius={30}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="revEnd"
                      paddingAngle={2}
                      animationBegin={0}
                      animationDuration={800}
                      animationEasing="ease-out"
                    >
                      {getPieChartData(topCountries).map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={getCountryColor(entry.country, index)}
                          stroke="#fff"
                          strokeWidth={2}
                          style={{
                            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                            transition: 'opacity 0.3s'
                          }}
                        />
                      ))}
                    </Pie>
                    <RechartsTooltip content={<CustomTooltip isDark={isDark} />} />
                    <Legend 
                      wrapperStyle={getLegendStyle(isDark)}
                      formatter={(value: any, entry: any) => [entry.payload.country]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </FullscreenChart>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                <Text>No country data available</Text>
                <br />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Try adjusting your filters or check the data source
                </Text>
              </div>
            )}
          </Card>
        </Col>

        {/* Country World Map */}
        <Col xs={24} lg={12}>
          <Card 
            title={
              <div style={{ margin: 0, padding: 0 }}>
                <Title level={5} style={{ margin: 0, marginBottom: 0 }}>Country Coverage Map</Title>
                <Text type="secondary" style={{ fontSize: '12px', margin: 0, display: 'block' }}>
                  Countries with data color-coded, remaining shown as RoW
                </Text>
              </div>
            }
            style={{ height: 460, display: 'flex', flexDirection: 'column' }}
            headStyle={{ padding: '12px 20px', minHeight: '72px', height: '72px' }}
            bodyStyle={{ padding: '8px', flex: 1 }}
          >
            <FullscreenChart
              chartId="country-map"
              title="Country Coverage Map"
              isFullscreen={fullscreenChart === 'country-map'}
              onToggleFullscreen={() => setFullscreenChart(fullscreenChart === 'country-map' ? null : 'country-map')}
              fullscreenContent={
                <div style={{ width: '100%', height: '100%' }}>
                  <CountryWorldMap
                    countryData={topCountries}
                    colorMap={COUNTRY_COLORS}
                  />
                </div>
              }
            >
              <CountryWorldMap
                countryData={topCountries}
                colorMap={COUNTRY_COLORS}
              />
            </FullscreenChart>
          </Card>
        </Col>

        {/* Top Countries by Revenue - Full Width */}
        <Col span={24}>
          <Card 
            title={
              <div>
                <Title level={5} style={{ margin: 0 }}>
                  Top Countries by Revenue
                </Title>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {filters.countries.length > 0 && filters.countries.length < 27 ? 
                    (filters.countries.length <= 8 ? 
                      `${filters.countries.length} selected countries` : 
                      `Top 8 selected countries`
                    ) : 
                    `Top 8`
                  }
                </Text>
              </div>
            }
            loading={chartLoading}
          >
            {topCountries.length > 0 ? (
              <div>
                <FullscreenChart
                  chartId="top-countries"
                  title="Top Countries by Revenue"
                  isFullscreen={fullscreenChart === 'top-countries'}
                  onToggleFullscreen={() => setFullscreenChart(fullscreenChart === 'top-countries' ? null : 'top-countries')}
                  fullscreenContent={
                                      <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={getTop8CountriesData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="country" 
                        angle={-45} 
                        textAnchor="end" 
                        height={80} 
                        tick={(props) => {
                          const { x, y, payload } = props;
                          const countryColor = getCountryColor(payload.value, 0);
                          return (
                            <g transform={`translate(${x},${y})`}>
                              <text
                                x={0}
                                y={0}
                                dy={16}
                                textAnchor="end"
                                fill={countryColor}
                                transform={`rotate(-45)`}
                                style={{
                                  fontFamily: getChartFontStyle(isDark).fontFamily,
                                  fontSize: getChartFontStyle(isDark).fontSize,
                                  fontWeight: getChartFontStyle(isDark).fontWeight,
                                  letterSpacing: getChartFontStyle(isDark).letterSpacing
                                }}
                              >
                                {payload.value}
                              </text>
                            </g>
                          );
                        }}
                      />
                      <YAxis tick={{ style: getChartFontStyle(isDark) }} />
                      <RechartsTooltip 
                        formatter={(value: any, name: string, props: any) => {
                          return [`$${Number(value).toFixed(1)}M`, `Revenue (${endYear})`];
                        }}
                        labelFormatter={(label: string) => label}
                      />
                      <Bar dataKey="revEnd" name={`Revenue (${endYear})`}>
                        {getTop8CountriesData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={getCountryColor(entry.country, index)} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  }
                >
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={getTop8CountriesData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="country" 
                        angle={-45} 
                        textAnchor="end" 
                        height={60} 
                        tick={(props) => {
                          const { x, y, payload } = props;
                          const countryColor = getCountryColor(payload.value, 0);
                          return (
                            <g transform={`translate(${x},${y})`}>
                              <text
                                x={0}
                                y={0}
                                dy={16}
                                textAnchor="end"
                                fill={countryColor}
                                transform={`rotate(-45)`}
                                style={{
                                  fontFamily: getChartFontStyle(isDark).fontFamily,
                                  fontSize: getChartFontStyle(isDark).fontSize,
                                  fontWeight: getChartFontStyle(isDark).fontWeight,
                                  letterSpacing: getChartFontStyle(isDark).letterSpacing
                                }}
                              >
                                {payload.value}
                              </text>
                            </g>
                          );
                        }}
                      />
                      <YAxis tick={{ style: getChartFontStyle(isDark) }} />
                      <RechartsTooltip 
                        formatter={(value: any, name: string, props: any) => {
                          return [`$${Number(value).toFixed(1)}M`, `Revenue (${endYear})`];
                        }}
                        labelFormatter={(label: string) => label}
                      />
                      <Bar dataKey="revEnd" name={`Revenue (${endYear})`}>
                        {getTop8CountriesData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={getCountryColor(entry.country, index)} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </FullscreenChart>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                <Text>No country data available</Text>
                <br />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Try adjusting your filters or check the data source
                </Text>
              </div>
            )}
          </Card>
        </Col>

        {/* Country Rankings Table */}
        <Col xs={24} lg={24}>
          <Card 
            title={
              <div>
                <Title level={5} style={{ margin: 0 }}>
                  Country Rankings
                </Title>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Detailed country performance metrics
                </Text>
              </div>
            }
            loading={chartLoading}
          >
            <FullscreenChart
              chartId="country-rankings"
              title="Country Rankings"
              isFullscreen={fullscreenChart === 'country-rankings'}
              onToggleFullscreen={() => setFullscreenChart(fullscreenChart === 'country-rankings' ? null : 'country-rankings')}
              fullscreenContent={
                <Table
                  columns={countryColumns}
                  dataSource={topCountries.filter(country => 
                    !['Others', 'Unknown', 'RoW'].includes(country.country)
                  )}
                  pagination={{ 
                    pageSize: 1000, // Show all results in fullscreen
                    size: 'default',
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} countries`
                  }}
                  size="middle"
                  rowKey="country"
                  scroll={{ y: 'calc(100vh - 200px)' }} // Full height in fullscreen
                  sortDirections={['descend', 'ascend']}
                  summary={() => {
                    // In fullscreen, show RoW at the end since we show all results
                    const rowCountry = topCountries.find(c => c.country === 'RoW');
                    if (!rowCountry) return null;
                    return (
                      <Table.Summary.Row style={{ background: '#fafafa' }}>
                        <Table.Summary.Cell index={0}>{rowCountry.rank}</Table.Summary.Cell>
                        <Table.Summary.Cell index={1}>
                          <div>
                            <Text strong>{rowCountry.country}</Text><br />
                            <Text type="secondary" style={{ fontSize: '12px' }}>{rowCountry.region}</Text>
                          </div>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={2}>{formatRevenue(rowCountry.revStart)}</Table.Summary.Cell>
                        <Table.Summary.Cell index={3}>{rowCountry.shareStart.toFixed(1)}%</Table.Summary.Cell>
                        <Table.Summary.Cell index={4}>{formatRevenue(rowCountry.revEnd)}</Table.Summary.Cell>
                        <Table.Summary.Cell index={5}>{rowCountry.shareEnd.toFixed(1)}%</Table.Summary.Cell>
                        <Table.Summary.Cell index={6}>
                          <span style={{ color: rowCountry.delta >= 0 ? '#52c41a' : '#f5222d' }}>
                            {formatDelta(rowCountry.delta)}
                          </span>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={7}>
                          <span style={{ color: '#52c41a' }}>{rowCountry.cagr.toFixed(1)}%</span>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    );
                  }}
                />
              }
            >
              <Table
                columns={countryColumns}
                dataSource={topCountries.filter(country => 
                  !['Others', 'Unknown', 'RoW'].includes(country.country)
                )}
                pagination={{ 
                  pageSize: 10, // Show exactly 10 results per page
                  size: 'small',
                  showSizeChanger: false, // Hide size changer
                  showQuickJumper: false, // Hide quick jumper
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} countries`,
                  onChange: (page) => setCurrentPage(page)
                }}
                size="small"
                rowKey="country"
                scroll={{ y: 400 }} // Increased height to show more entries
                sortDirections={['descend', 'ascend']}
                summary={() => {
                  const rowCountry = topCountries.find(c => c.country === 'RoW');
                  if (!rowCountry) return null;
                  
                  // Calculate if we're on the last page
                  const filteredCountries = topCountries.filter(country => 
                    !['Others', 'Unknown', 'RoW'].includes(country.country)
                  );
                  const pageSize = 10;
                  const totalPages = Math.ceil(filteredCountries.length / pageSize);
                  
                  // Only show RoW summary on the last page
                  if (currentPage !== totalPages) return null;
                  
                  return (
                    <Table.Summary.Row style={{ background: '#fafafa' }}>
                      <Table.Summary.Cell index={0}>{rowCountry.rank}</Table.Summary.Cell>
                      <Table.Summary.Cell index={1}>
                        <div>
                          <Text strong>{rowCountry.country}</Text><br />
                          <Text type="secondary" style={{ fontSize: '12px' }}>{rowCountry.region}</Text>
                        </div>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2}>{formatRevenue(rowCountry.revStart)}</Table.Summary.Cell>
                      <Table.Summary.Cell index={3}>{rowCountry.shareStart.toFixed(1)}%</Table.Summary.Cell>
                      <Table.Summary.Cell index={4}>{formatRevenue(rowCountry.revEnd)}</Table.Summary.Cell>
                      <Table.Summary.Cell index={5}>{rowCountry.shareEnd.toFixed(1)}%</Table.Summary.Cell>
                      <Table.Summary.Cell index={6}>
                        <span style={{ color: rowCountry.delta >= 0 ? '#52c41a' : '#f5222d' }}>
                          {formatDelta(rowCountry.delta)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={7}>
                        <span style={{ color: '#52c41a' }}>{rowCountry.cagr.toFixed(1)}%</span>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  );
                }}
              />
            </FullscreenChart>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default CountryInsights;